import jwt_decode from 'jwt-decode';
import { toast } from '../components';

/**
 * Checks if a JWT token is expired
 *
 * @param token - The JWT token to validate
 * @returns true if the token is expired, missing, or invalid; false otherwise
 */
export function isJwtExpired(token: string | undefined | null): boolean {
    if (!token) return true;

    try {
        const decoded = jwt_decode<{ exp?: number }>(token);

        if (!decoded.exp) return true;

        const now = Math.floor(Date.now() / 1000); // current time in seconds
        return decoded.exp < now;
    } catch (e) {
        // Invalid token format
        console.error('JWT validation error:', e);
        return true;
    }
}

/**
 * Checks if a JWT token is expired and shows an error toast if validation fails
 *
 * @param token - The JWT token to validate
 * @param toastId - Optional toast ID to prevent duplicate toasts
 * @returns true if the token is expired, missing, or invalid; false otherwise
 */
export function isJwtExpiredWithToast(token: string | undefined | null, toastId: string = 'jwt-validation'): boolean {
    if (!token) return true;

    try {
        const decoded = jwt_decode<{ exp?: number }>(token);

        if (!decoded.exp) return true;

        const now = Math.floor(Date.now() / 1000);
        return decoded.exp < now;
    } catch (e: any) {
        toast.error(e?.message || 'Token validation failed', { toastId });
        return true;
    }
}

/**
 * Decodes a JWT token and extracts the email
 *
 * @param token - The JWT token to decode
 * @returns The email from the token, or null if not found
 */
export function getEmailFromToken(token: string | undefined | null): string | null {
    if (!token) return null;

    try {
        const decoded = jwt_decode<{ email?: string }>(token);
        return decoded.email || null;
    } catch (e) {
        console.error('Failed to decode token:', e);
        return null;
    }
}
